import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import './ReadFlights.css';
import './FlightGraphsLayout.css';
import GraphPlaceholder, { fetchGraphDataCached } from './GraphPlaceholder';
import SynchronizedVideo from './SynchronizedVideo';
import UploadVideoModal from './UploadVideoModal';
import FlightMap from './FlightMap';
import CustomVariableModal from './CustomVariableModal';
import { authFetch, isInteractiveAuthError } from '../authFetch';

// Endpoint providing flight summaries (uuid, name, location, date, columns)
const FLIGHT_SUMMARIES_API_ENDPOINT = process.env.REACT_APP_FLIGHT_SUMMARIES_API_ENDPOINT ||
  'https://78q545moi7.execute-api.eu-west-2.amazonaws.com/flightSummaries-dev';

export const PLACEHOLDER_TELEMETRY_OPTS = [
  { value: 'velocity', label: 'Velocity' },
  { value: 'altitude', label: 'Altitude' },
  { value: 'yaw', label: 'Yaw' },
  { value: 'pitch', label: 'Pitch' },
  { value: 'roll', label: 'Roll' },
  { value: 'battery', label: 'Battery' },
  { value: 'temperature', label: 'Temperature' },
];

function FlightGraphs() {
  const location = useLocation();
  const preselectFlightUuid = location?.state?.preselectFlightUuid;
  const preselectFlightTestName = location?.state?.preselectFlightTestName;
  const [graphs, setGraphs] = useState([]); // {id, flightUuid, columns[]}
  const [videos, setVideos] = useState([]); // {id,url,offset}
  const [globalTime, setGlobalTime] = useState(0);
  
  // Keep globalTimeRef in sync with globalTime state
  useEffect(() => {
    globalTimeRef.current = globalTime;
  }, [globalTime]);
  const [playing, setPlaying] = useState(false);
  

  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [flightOptions, setFlightOptions] = useState([]); // {value,label,columns[]}
  const [selectedFlight, setSelectedFlight] = useState('');
  const [userHasSelectedFlight, setUserHasSelectedFlight] = useState(false); // prevent auto-select override
  const [toastMsg, setToastMsg] = useState(''); // ephemeral toast message
  // Start with no telemetry options; only populate once a flight is chosen.
  const [telemetryOptions, setTelemetryOptions] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState([]); // react-select objects
  const [showVideosPane, setShowVideosPane] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [modalFlightUuid] = useState('');
  const [flightSearch, setFlightSearch] = useState('');
  const [variableSearch, setVariableSearch] = useState('');
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customVariables, setCustomVariables] = useState([]); // {name, expression, dependencies}
  const wasPlayingRef = useRef(false);
  const globalTimeRef = useRef(0);
  const [domainStart, setDomainStart] = useState(null);
  const [domainEnd, setDomainEnd] = useState(null);
  // Flight-relative domain (launch to flight end) separate from raw logging domain
  const [launchTime, setLaunchTime] = useState(null);
  const [flightEndTime, setFlightEndTime] = useState(null);
  const liveStatusRef = useRef({}); // flight_uuid -> { status, seenAt }
  // Routing: which flights should use livestream endpoint (current live or formerly livestreamed)
  const liveRouteRef = useRef({}); // { [flight_uuid]: boolean }
  const [routingVersion, setRoutingVersion] = useState(0); // bump to force re-render after route map update
  const shouldUseLiveApi = useCallback((uuid) => {
    const route = liveRouteRef.current[uuid];
    const result = route === 'live' || route === 'finished_livestream';
    // console.log('[shouldUseLiveApi]', { uuid, route, result });
    return result;
  }, []);
  
  const isFinishedLivestream = useCallback((uuid) => {
    const result = liveRouteRef.current[uuid] === 'finished_livestream';
    // console.log('[isFinishedLivestream]', { uuid, route: liveRouteRef.current[uuid], result });
    return result;
  }, []);
  
  // Heuristic detector for flights that were formerly livestreamed (but may have no current heartbeat)
  // (keeping detection logic in routing classification instead)

  // Derive a crude max duration based on current graphs or videos (extend later with actual max telemetry time)
  // Flight duration based on launch->flightEnd; fall back to raw domain if launch not yet known
  const maxDuration = useMemo(() => {
    if (launchTime != null && flightEndTime != null && flightEndTime > launchTime) return flightEndTime - launchTime;
    if (domainStart != null && domainEnd != null && domainEnd > domainStart) return domainEnd - domainStart;
    return 0;
  }, [launchTime, flightEndTime, domainStart, domainEnd]);

  // Normalized time (0 at launchTime if known, else at raw domainStart)
  const baseZero = (launchTime != null ? launchTime : domainStart);
  const normalizedTime = baseZero != null ? Math.max(0, globalTime - baseZero) : 0;
  const clampedGlobalTime = Math.min(normalizedTime, maxDuration || normalizedTime);

  const handleScrubStart = () => {
    wasPlayingRef.current = playing;
    if (playing) setPlaying(false);
  };
  const handleScrub = (e) => {
    const val = Number(e.target.value);
    if (!Number.isNaN(val)) {
      if (launchTime != null) {
        setGlobalTime(launchTime + val);
      } else if (domainStart != null) {
        setGlobalTime(domainStart + val);
      } else {
        setGlobalTime(val);
      }
    }
  };
  const handleScrubEnd = () => {
    if (wasPlayingRef.current) setPlaying(true);
  };

  const formatTime = (t) => {
    if (!Number.isFinite(t)) return '0:00';
    const s = Math.max(0, Math.floor(t));
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return `${m}:${rem.toString().padStart(2,'0')}`;
  };

  // Removed react-select usage; keeping placeholder for future styling if reintroduced.

  // Fetch flight summaries for dropdown & columns
  // NOTE: We intentionally only refetch summaries when date filters change.
  // selectedFlight & userHasSelectedFlight are read only for optional auto-select logic after fetch.
  // Adding them as deps would cause unnecessary refetches or loops when auto-select sets state.
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        let url = FLIGHT_SUMMARIES_API_ENDPOINT;
        const qs = [];
        if (filterStart) qs.push(`startDate=${encodeURIComponent(filterStart)}`);
        if (filterEnd) qs.push(`endDate=${encodeURIComponent(filterEnd)}`);
        if (qs.length) url += `?${qs.join('&')}`;
        const resp = await authFetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  const rows = await resp.json();

  // Heartbeat logging: compute delta between last heartbeat and now (include all flights)
        try {
          const nowSec = Date.now() / 1000;
          const toSeconds = (val) => {
            if (val == null) return null;
            if (typeof val === 'number') { if (!Number.isFinite(val)) return null; return val > 1e12 ? val/1000 : val; }
            if (typeof val === 'string') {
              const n = Number(val); if (Number.isFinite(n)) return n > 1e12 ? n/1000 : n;
              const d = Date.parse(val); if (!isNaN(d)) return d/1000; return null;
            }
            return null;
          };
          const entries = rows.map(r => {
            const name = r.flight_name || r.flightName || 'Unknown';
            const raw = r.last_hearbeat ?? r.last_heartbeat ?? r.lastHeartbeat;
            const hbSec = toSeconds(raw);
            if (hbSec == null) return { name, flight_uuid: r.flight_uuid, hbSec: null, delta: null, status: 'No Data' };
            const delta = nowSec - hbSec;
            return { name, flight_uuid: r.flight_uuid, hbSec, delta, status: delta > 30 ? 'Dead Flight' : 'Live Flight' };
          }).filter(e => e);
          entries.sort((a,b)=>{
            if (a.delta==null && b.delta==null) return 0;
            if (a.delta==null) return 1;
            if (b.delta==null) return -1;
            return a.delta - b.delta;
          });
          if (entries.length) {
            const allNoData = entries.every(e => e.hbSec == null);
            if (allNoData) {
              try {
                console.groupCollapsed('[Heartbeat][Diag] All flights show No Data – inspecting raw rows');
                rows.slice(0,5).forEach((r,i) => {
                  const keys = Object.keys(r).sort();
                  const rawVal = r.last_hearbeat ?? r.last_heartbeat ?? r.lastHeartbeat;
                  console.log(`Row#${i} name="${r.flight_name}" rawHeartbeat=`, rawVal, 'keys=', keys);
                });
                console.groupEnd();
              } catch(diagErr) {}
            }
            console.groupCollapsed(`[Heartbeat] ${entries.length} flights @ ${new Date().toLocaleTimeString()}`);
            entries.slice(0,8).forEach(e => {
              if (e.delta==null) {
                console.log(`name="${e.name}" heartbeat=NONE status=${e.status}`);
              } else {
                console.log(`name="${e.name}" delta=${e.delta.toFixed(1)}s status=${e.status}`);
              }
            });
            console.groupEnd();

            // Update live status cache
            const map = { ...liveStatusRef.current };
            entries.forEach(e => { if (e.flight_uuid) map[e.flight_uuid] = { status: e.status, seenAt: Date.now(), delta: e.delta }; });
            liveStatusRef.current = map;

            // Build routing map: live vs finished_livestream vs historical
            try {
              const liveNowSet = new Set(entries.filter(e => e.status === 'Live Flight').map(e => e.flight_uuid));
              const routing = {};
              
              rows.forEach(r => {

                console.log('Pos 1')
                console.log('r:', r);
                console.log('uploader_name:', r.uploader_name, 'type:', typeof r.uploader_name);
                console.log('Pos 2')
                const uuid = r.flight_uuid;
                if (!uuid) return;
                
                if (liveNowSet.has(uuid)) {
                  routing[uuid] = 'live';
                  console.log('[Routing] LIVE:', { uuid, name: r.flight_name, uploader: r.uploader_name, delta: entries.find(e => e.flight_uuid === uuid)?.delta });
                } else if (r.uploader_name === 'Bot') {
                  routing[uuid] = 'finished_livestream';
                  console.log('[Routing] FINISHED_LIVESTREAM:', { uuid, name: r.flight_name, uploader: r.uploader_name, delta: entries.find(e => e.flight_uuid === uuid)?.delta });
                } else {
                  routing[uuid] = 'historical';
                  console.log('[Routing] HISTORICAL:', { uuid, name: r.flight_name, uploader: r.uploader_name, delta: entries.find(e => e.flight_uuid === uuid)?.delta });
                }
              });
              
              liveRouteRef.current = routing;
              setRoutingVersion(v => v + 1);
              
              // Enhanced diagnostics
              const routeSummary = Object.entries(routing).reduce((acc, [uuid, route]) => {
                acc[route] = (acc[route] || 0) + 1;
                return acc;
              }, {});
              console.log('[Routing] Summary:', routeSummary);
              
              const sample = Object.entries(routing).slice(0,5).map(([k,v])=>`${k}:${v}`).join(', ');
              console.log('[Routing] sample', sample);
            } catch(routeErr) { 
              console.error('[Routing] Error:', routeErr);
            }

            // Auto-select only truly live flights (not finished livestreams)
            if (!userHasSelectedFlight && !selectedFlight) {
              const live = entries.filter(e => e.status === 'Live Flight' && e.hbSec != null);
              if (live.length) {
                // live entries are sorted by delta already (ascending) so first is most recent
                const best = live[0];
                if (best.flight_uuid) {
                  setSelectedFlight(best.flight_uuid);
                  try { console.log('[Heartbeat][AutoSelect] Selected live flight', best.name, best.flight_uuid, 'delta_s=', best.delta.toFixed(1)); } catch(_) {}
                  // Trigger toast to inform user a live flight was auto-selected
                  setToastMsg('Live Flight');
                }
              }
            }
          }
        } catch(hbErr) { /* ignore heartbeat logging errors */ }

        const opts = rows.map(r => {
          const datePart = r.flight_date ? String(r.flight_date).slice(0,10) : 'Unknown';
          const namePart = r.flight_name || 'Unknown';
            const locPart  = r.flight_location || 'Unknown';
          return { value: r.flight_uuid, label: `${namePart} - ${locPart} - ${datePart}` , columns: Array.isArray(r.columns)? r.columns: [] };
        });
        setFlightOptions(opts);
        // If we have a preselect flight from navigation and not yet chosen, select it (suppress auto selection)
        if (preselectFlightUuid && !selectedFlight && opts.some(o=>o.value===preselectFlightUuid)) {
          setSelectedFlight(preselectFlightUuid);
          setUserHasSelectedFlight(true); // prevent auto live override
          console.log('[Preselect] Applied flight from navigation state', {
            flight_uuid: preselectFlightUuid,
            test_name: preselectFlightTestName || 'N/A'
          });
        }
      } catch (err) {
        if (isInteractiveAuthError(err)) return;
        console.error('Error fetching flight summaries:', err);
        setFlightOptions([]);
      }
    };
    fetchSummaries();
  }, [filterStart, filterEnd]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Periodic heartbeat refresh every 30s to keep live status up to date
  // This is lighter weight than full summaries fetch - just updates heartbeat deltas
  useEffect(() => {
    if (!flightOptions.length) return; // No flights to check
    
    const refreshHeartbeats = async () => {
      try {
        console.log('[HeartbeatRefresh] Checking heartbeats for', flightOptions.length, 'flights');
        let url = FLIGHT_SUMMARIES_API_ENDPOINT;
        const qs = [];
        if (filterStart) qs.push(`startDate=${encodeURIComponent(filterStart)}`);
        if (filterEnd) qs.push(`endDate=${encodeURIComponent(filterEnd)}`);
        if (qs.length) url += `?${qs.join('&')}`;
        
        const resp = await authFetch(url);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const rows = await resp.json();
        
        // Update just the heartbeat cache
        const nowSec = Date.now() / 1000;
        const toSeconds = (val) => {
          if (val == null) return null;
          if (typeof val === 'number') { if (!Number.isFinite(val)) return null; return val > 1e12 ? val/1000 : val; }
          if (typeof val === 'string') {
            const n = Number(val); if (Number.isFinite(n)) return n > 1e12 ? n/1000 : n;
            const d = Date.parse(val); if (!isNaN(d)) return d/1000; return null;
          }
          return null;
        };
        
        const map = { ...liveStatusRef.current };
        rows.forEach(r => {
          const hbSec = toSeconds(r.last_hearbeat ?? r.last_heartbeat ?? r.lastHeartbeat);
          if (hbSec != null && r.flight_uuid) {
            const delta = nowSec - hbSec;
            const status = delta > 30 ? 'Dead Flight' : 'Live Flight';
            map[r.flight_uuid] = { status, seenAt: Date.now(), delta };
          }
        });
        liveStatusRef.current = map;
        
        console.log('[HeartbeatRefresh] Updated heartbeats for', Object.keys(map).length, 'flights');
      } catch (err) {
        console.warn('[HeartbeatRefresh] Failed:', err);
      }
    };
    
    // Immediate refresh, then every 30s
    refreshHeartbeats();
    const interval = setInterval(refreshHeartbeats, 30000);
    return () => clearInterval(interval);
  }, [flightOptions.length, filterStart, filterEnd, selectedFlight]);

  // Update telemetry options when selected flight changes
  useEffect(() => {
    // When flight changes, clear current variable selections and videos.
    setSelectedSeries([]);
    if (!selectedFlight) {
      setTelemetryOptions([]);
      return;
    }
    const chosen = flightOptions.find(o => o.value === selectedFlight);
    if (chosen && Array.isArray(chosen.columns) && chosen.columns.length) {
      setTelemetryOptions(chosen.columns.map(c => ({ value: c, label: c })));
    } else {
      setTelemetryOptions([]);
    }
  }, [selectedFlight, flightOptions]);

  // Prefetch graph data for caching (expand custom variables to their dependencies)
  // Skip prefetching for finished livestreams to avoid unwanted graph updates during variable selection
  useEffect(() => {
    if (!selectedFlight || selectedSeries.length === 0) return;
    
    // Skip prefetching for finished livestreams - user should control graph updates explicitly
    const isFinished = isFinishedLivestream(selectedFlight);
    if (isFinished) {
      console.log('[Prefetch] SKIPPED for finished livestream:', selectedFlight, 'selectedSeries:', selectedSeries.length);
      return;
    }
    
    const baseCols = new Set();
    selectedSeries.forEach(o => {
      const name = o.value;
      const cv = customVariables.find(cv => cv.name === name);
      if (cv) {
        cv.dependencies.forEach(d => baseCols.add(d));
      } else {
        baseCols.add(name);
      }
    });
    if (baseCols.size === 0) return;
    
    const useLive = shouldUseLiveApi(selectedFlight);
    console.log('[Prefetch] ENABLED for live/historical flight:', selectedFlight, 'columns:', Array.from(baseCols));
    fetchGraphDataCached(
      selectedFlight,
      Array.from(baseCols),
      { live: useLive, liveStrict: useLive, allowLiveFallback: false }
    ).catch(()=>{});
  }, [selectedFlight, selectedSeries, customVariables, shouldUseLiveApi, routingVersion, isFinishedLivestream]);

  const handleCreateGraph = () => {
    const fieldsArr = selectedSeries.map(o => o.value);
    if (!selectedFlight || fieldsArr.length === 0) return;
    setGraphs(prev => [...prev, { id: Date.now(), flightUuid: selectedFlight, columns: fieldsArr }]);
    setSelectedSeries([]);
  };

  const fetchVideos = useCallback(async (flightUuid) => {
    if (!flightUuid) return;
    try {
      const res = await authFetch(`https://0ynzhe2caa.execute-api.eu-west-2.amazonaws.com/listFlightVideos-dev?flight_uuid=${flightUuid}`);
      if (!res.ok) throw new Error('failed');
      const list = await res.json();
      setVideos(list.map(v => ({ id: v.video_uuid, url: v.url, offset: v.video_offset_s || 0 })));
      if (!showVideosPane && list.length && list[0].launch_offset_s != null) {
        setGlobalTime(list[0].launch_offset_s);
      }
    } catch (e) { console.error(e); }
  }, [showVideosPane]);

  const handleRemoveVideo = (id) => setVideos(vs => vs.filter(v => v.id !== id));
  
  // Throttle user seeks to prevent feedback loops
  const lastSeekTimeRef = useRef(0);
  const handleUserSeek = useCallback((t) => {
    const now = Date.now();
    // Prevent rapid successive seeks (less than 100ms apart)
    if (now - lastSeekTimeRef.current < 100) {
      return;
    }
    lastSeekTimeRef.current = now;
    
    setGlobalTime(prev => {
      let next = t;
      if (launchTime != null && next < launchTime) next = launchTime;
      if (flightEndTime != null && next > flightEndTime) next = flightEndTime;
      return next;
    });
  }, [launchTime, flightEndTime]);
  
  const handleUserPlayPause = (p) => setPlaying(p);
  const handleRemoveGraph = (gid) => setGraphs(prev => prev.filter(g => g.id !== gid));

  // Master playback ticker: drives time updates for both graphs and videos
  useEffect(() => {
    if (!playing) return;
    // Need at least some domain
    const effectiveStart = launchTime != null ? launchTime : domainStart;
    const effectiveEnd = flightEndTime != null ? flightEndTime : domainEnd;

    if (effectiveStart == null || effectiveEnd == null) return;
    
    let frame;
    let lastTs;
    let isRunning = true; // Use local flag instead of stale closure
    
    const step = (ts) => {
      if (!isRunning) return; // Check local flag instead of stale playing state
      if (lastTs == null) lastTs = ts;
      
      // Calculate time delta, but cap it to prevent large jumps
      let dt = (ts - lastTs) / 1000;
      // Cap delta to maximum of 50ms (0.05 seconds) for smoother playback
      // Also ensure minimum frame time to prevent too frequent updates
      dt = Math.max(0.016, Math.min(dt, 0.05)); // 16ms to 50ms range
      
      lastTs = ts;
      setGlobalTime(t => {
        let next = t + dt;
        if (next > effectiveEnd) {
          setPlaying(false);
          isRunning = false; // Stop local loop
          return effectiveEnd;
        }
        return next;
      });
      
      // Continue animation if still running
      if (isRunning) {
        frame = requestAnimationFrame(step);
      }
    };
    
    frame = requestAnimationFrame(step);
    return () => { 
      isRunning = false; // Stop the loop
      if (frame) cancelAnimationFrame(frame); 
    };
  }, [playing, launchTime, flightEndTime, domainStart, domainEnd]);

  // Clamp globalTime whenever launch/flight boundaries shift (e.g., late arriving domain info)
  useEffect(() => {
    setGlobalTime(t => {
      let next = t;
      if (launchTime != null && next < launchTime) next = launchTime;
      if (flightEndTime != null && next > flightEndTime) next = flightEndTime;
      return next;
    });
  }, [launchTime, flightEndTime]);

  // Reset videos display when flight changes
  useEffect(() => { 
    setShowVideosPane(false); 
    setVideos([]); 
    // Reset domain on flight change
    setDomainStart(null);
    setDomainEnd(null);
    setLaunchTime(null);
    setFlightEndTime(null);
    setGlobalTime(0);
    // Clear existing graphs on flight change so defaults can repopulate
    setGraphs([]);
    // Reset custom variables (scoped per flight)
    setCustomVariables([]);
  }, [selectedFlight]);

  // Inject default graphs when a flight is selected and telemetry columns known.
  // Live flights (current or formerly livestreamed) get a different default set.
  useEffect(() => {
    if (!selectedFlight) return;
    if (!telemetryOptions || telemetryOptions.length === 0) return;
    if (graphs.length > 0) return; // already initialized for this flight

    const hasCol = (col) => telemetryOptions.some(o => o.value === col);
    const defaults = [];
    const route = liveRouteRef.current[selectedFlight];
    const useLiveDefaults = route === 'live' || route === 'finished_livestream';
    
    console.log('[DefaultGraphs]', { selectedFlight, route, useLiveDefaults, telemetryCount: telemetryOptions.length });

    if (useLiveDefaults) {
      console.log('[DefaultGraphs] Using LIVE defaults for route:', route);
      // Live defaults for both live and finished livestreams: each variable gets its own graph (if present)
      const LIVE_DEFAULTS = ['altitude','airspeed','throttle','nav_state','g_loading','climb_rate'];
      LIVE_DEFAULTS.forEach((col, idx) => {
        if (hasCol(col)) {
          defaults.push({ id: Date.now() + idx + 100, flightUuid: selectedFlight, columns: [col] });
          console.log('[DefaultGraphs] Added live default:', col);
        } else {
          console.log('[DefaultGraphs] Live default not available:', col);
        }
      });
      // Add one combined RC inputs graph if all four inputs are available
      const rcInputs = ['rc_roll','rc_pitch','rc_yaw','rc_throttle'];
      if (rcInputs.every(c => hasCol(c))) {
        defaults.push({ id: Date.now() + 199, flightUuid: selectedFlight, columns: rcInputs });
        console.log('[DefaultGraphs] Added RC inputs graph');
      }
      
      // Add flight mode and arm status graph
      const modeArmInputs = ['rc_flight_mode','rc_arm'];
      if (modeArmInputs.every(c => hasCol(c))) {
        defaults.push({ id: Date.now() + 200, flightUuid: selectedFlight, columns: modeArmInputs });
        console.log('[DefaultGraphs] Added flight mode and arm status graph');
      }
      // Fallback: if none of the live defaults exist, fall back to historical defaults below
      if (defaults.length === 0) {
        console.log('[DefaultGraphs] No live defaults available, trying fallback');
        const accelCol = 'vehicle_local_position.ax';
        if (hasCol(accelCol)) {
          defaults.push({ id: Date.now() + 1, flightUuid: selectedFlight, columns: [accelCol] });
          console.log('[DefaultGraphs] Added fallback default:', accelCol);
        }
      }
    } else {
      console.log('[DefaultGraphs] Using HISTORICAL defaults for route:', route);
      // Historical defaults
      const accelCol = 'vehicle_local_position.ax';
      if (hasCol(accelCol)) {
        defaults.push({ id: Date.now() + 1, flightUuid: selectedFlight, columns: [accelCol] });
        console.log('[DefaultGraphs] Added historical default:', accelCol);
      }
      const xyzCols = ['vehicle_local_position.x','vehicle_local_position.y','vehicle_local_position.z'];
      if (xyzCols.every(c => hasCol(c))) {
        defaults.push({ id: Date.now() + 2, flightUuid: selectedFlight, columns: xyzCols });
        console.log('[DefaultGraphs] Added XYZ position graph');
      }
    }

    if (defaults.length) setGraphs(defaults);
  }, [selectedFlight, telemetryOptions, graphs.length, shouldUseLiveApi, routingVersion]);

  // Auto-load videos when a flight is selected
  useEffect(() => {
    if (selectedFlight) {
      console.log('[FlightGraphs] Auto-loading videos for flight:', selectedFlight);
      fetchVideos(selectedFlight);
      setShowVideosPane(true); // Automatically show the video pane
    }
  }, [selectedFlight, fetchVideos]);

  // Auto-dismiss toast after 2.5s
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToastMsg(''), 2500);
    return () => clearTimeout(t);
  }, [toastMsg]);

  return (
    <div className="graphs-page-wrapper">
      {/* Top-left: Graphs */}
      <div className="graphs-cell graphs-main">
        <div className="graphs-cell-header">
          {/* Removed per master control bar; could retain a small sync indicator if desired */}
        </div>
        <div className="graphs-main-scroll">
          {graphs.map(g => {
            const useLive = shouldUseLiveApi(g.flightUuid);
            const isFinished = isFinishedLivestream(g.flightUuid);
            const route = liveRouteRef.current[g.flightUuid];
            
            // Add heartbeat-based fallback for disabling polling
            const meta = liveStatusRef.current[g.flightUuid];
            const heartbeatTooOld = meta && meta.delta != null && meta.delta > 30;
            const shouldDisablePolling = isFinished || heartbeatTooOld;
            
            // Prevent data refetch for both finished livestreams AND live flights
            // This stops existing graphs from reloading when variables are selected
            const shouldPreventRefetch = isFinished || useLive;
            
            console.log('[GraphRender]', { 
              flight: g.flightUuid, 
              route, 
              useLive, 
              isFinished, 
              heartbeatTooOld,
              heartbeatDelta: meta?.delta,
              columns: g.columns,
              disablePolling: shouldDisablePolling,
              preventDataRefetch: shouldPreventRefetch
            });
            
            if (heartbeatTooOld && !isFinished) {
              console.log('[GraphRender] Disabling polling due to old heartbeat:', { 
                flight: g.flightUuid, 
                delta: meta.delta, 
                status: meta.status 
              });
            }
            
            return (
              <GraphPlaceholder
                key={g.id}
                id={g.id}
                flightUuid={g.flightUuid}
                columns={g.columns}
                currentTime={globalTime}
                playing={playing}
                onTogglePlay={()=>setPlaying(p=>!p)}
                onUserSeek={(t)=>handleUserSeek(t)}
                onResetGlobalTime={(t)=>setGlobalTime(t)}
                onRemove={() => handleRemoveGraph(g.id)}
                onDomain={({ xMin, xMax, launchOffset }) => {
                  // Raw logging domain aggregation (keep for reference if needed)
                  setDomainStart(prev => (prev == null || xMin < prev ? xMin : prev));
                  setDomainEnd(prev => (prev == null || xMax > prev ? xMax : prev));
                  // Flight-relative domain: prefer launchOffset as start if provided
                  const candidateLaunch = (launchOffset != null && Number.isFinite(launchOffset)) ? launchOffset : xMin;
                  setLaunchTime(prev => (prev == null || candidateLaunch < prev ? candidateLaunch : prev));
                  setFlightEndTime(prev => (prev == null || xMax > prev ? xMax : prev));
                  // Initialize globalTime at launch if not yet aligned
                  if (launchOffset != null && Number.isFinite(launchOffset) && globalTime < launchOffset) {
                    setGlobalTime(launchOffset);
                  }
                }}
                customVariables={customVariables}
                live={useLive}
                liveStrict={useLive}
                allowLiveFallback={false}
                disablePolling={shouldDisablePolling}
                heartbeatDelta={meta?.delta}
                preventDataRefetch={shouldPreventRefetch}
              />
            );
          })}
          {graphs.length === 0 && (
            <div style={{ padding:'12px', fontSize:'0.8rem', color:'#444' }}>No graphs yet. Select a flight & series below and click "Create Graph".</div>
          )}
        </div>
      </div>
      {/* Top-right: Videos */}
      <div className="graphs-cell videos-pane">
        <div className="graphs-cell-header">
          <div style={{ display:'flex', gap:'6px' }}>
            {videos.length>0 && (
              <button className="toggle-video-button" type="button" onClick={()=>setShowVideosPane(p=>!p)}>{showVideosPane ? 'Hide' : 'Show'}</button>
            )}
            {selectedFlight && (
              <button className="toggle-video-button" type="button" onClick={()=>fetchVideos(selectedFlight)}>Refresh Videos</button>
            )}
          </div>
        </div>
        <div className="videos-scroll">
          {showVideosPane && videos.length>0 ? (
            videos.map(v => (
              <div key={v.id} className="video-wrapper">
                <SynchronizedVideo
                  src={v.url}
                  currentTime={Math.max(0, globalTime - (v.offset || 0))}
                  playing={playing}
                  onUserSeek={(t)=>handleUserSeek(t + (v.offset || 0))}
                  onUserPlayPause={handleUserPlayPause}
                  offset={v.offset || 0}
                  globalTime={globalTime}
                  videoId={v.id}
                />
                <button className="video-remove-btn" onClick={()=>handleRemoveVideo(v.id)} title="Remove video">×</button>
              </div>
            ))
          ) : (
            <div style={{ padding:'10px', fontSize:'0.75rem', color:'#555' }}>No videos loaded.</div>
          )}
        </div>
      </div>
      {/* Bottom-left: Controls / Selection (split into Flights + Variables lists) */}
      <div className="graphs-cell controls-pane no-top-header">
        <div className="controls-split-wrapper">
          <div className="controls-split flights-panel">
            <div className="panel-header-row">
              <span className="panel-title">Flights</span>
              <div className="panel-filters">
                <input
                  type="date"
                  className="filter-input compact"
                  value={filterStart}
                  onChange={e=>setFilterStart(e.target.value)}
                  title="Filter Start"
                />
                <input
                  type="date"
                  className="filter-input compact"
                  value={filterEnd}
                  onChange={e=>setFilterEnd(e.target.value)}
                  title="Filter End"
                />
                <input
                  type="text"
                  className="filter-input compact flight-search-input"
                  placeholder="Search..."
                  value={flightSearch}
                  onChange={e=>setFlightSearch(e.target.value)}
                  title="Search flights"
                />
              </div>
            </div>
            <div className="flights-scroll">
              {flightOptions.length === 0 && (
                <div className="empty-hint">No flights</div>
              )}
              {flightOptions
                .filter(opt => !flightSearch || opt.label.toLowerCase().includes(flightSearch.toLowerCase()))
                .map(opt => {
                const active = opt.value === selectedFlight;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`flight-row-btn ${active ? 'active' : ''}`}
                    onClick={()=>{ 
                      console.log('Flight Selected:', opt.label, 'UUID:', opt.value);
                      setUserHasSelectedFlight(true); 
                      setSelectedFlight(opt.value); 
                    }}
                    title={opt.label}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="controls-split variables-panel">
            <div className="panel-header-row">
              <div className="panel-left">
                <span className="panel-title">Variables</span>
                <input
                  type="text"
                  className="filter-input compact variable-search-input"
                  placeholder="Search..."
                  value={variableSearch}
                  onChange={e=>setVariableSearch(e.target.value)}
                  title="Search variables"
                  disabled={!selectedFlight || telemetryOptions.length===0}
                />
              </div>
              <div style={{display:'flex', gap:'6px'}}>
                <button
                  type="button"
                  className="panel-action-btn"
                  disabled={!selectedFlight || telemetryOptions.length===0 || selectedSeries.length===0}
                  onClick={handleCreateGraph}
                  title={!selectedFlight ? 'Select a flight first' : (telemetryOptions.length===0 ? 'No variables available' : (selectedSeries.length===0 ? 'Select variables to graph' : 'Create graph with selected variables'))}
                >Create Graph</button>
                <button
                  type="button"
                  className="panel-action-btn"
                  disabled={!selectedFlight}
                  onClick={()=>setShowCustomModal(true)}
                  title={!selectedFlight ? 'Select a flight first' : 'Create a custom derived variable'}
                >Custom</button>
              </div>
            </div>
            <div className="variables-scroll">
              {(!telemetryOptions || telemetryOptions.length===0) && (
                <div className="empty-hint">{selectedFlight ? 'No variables' : 'Select a flight'}</div>
              )}
              {telemetryOptions
                .filter(opt => !variableSearch || opt.label.toLowerCase().includes(variableSearch.toLowerCase()))
                .map(opt => {
                const active = selectedSeries.some(s=>s.value===opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`variable-row-btn ${active ? 'active' : ''}`}
                    onClick={()=> {
                      setSelectedSeries(prev => {
                        const exists = prev.some(p=>p.value===opt.value);
                        if (exists) { return prev.filter(p=>p.value!==opt.value); }
                        return [...prev, opt];
                      });
                    }}
                    title={opt.label}
                  >
                    {opt.label}
                  </button>
                );
              })}
              {customVariables.length > 0 && (
                <div className="custom-vars-section" style={{marginTop:'6px', borderTop:'1px solid #ccc', paddingTop:'4px'}}>
                  {customVariables
                    .filter(cv => !variableSearch || cv.name.toLowerCase().includes(variableSearch.toLowerCase()))
                    .map(cv => {
                      const active = selectedSeries.some(s=>s.value===cv.name);
                      return (
                        <button
                          key={cv.name}
                          type="button"
                          className={`variable-row-btn ${active ? 'active' : ''}`}
                          onClick={()=> {
                            setSelectedSeries(prev => {
                              const exists = prev.some(p=>p.value===cv.name);
                              if (exists) { return prev.filter(p=>p.value!==cv.name); }
                              return [...prev, { value: cv.name, label: cv.name, _custom: true }];
                            });
                          }}
                          title={cv.expression}
                        >{cv.name}</button>
                      );
                    })}
                </div>
              )}
            </div>
            <div className="selected-summary" aria-live="polite">
              {selectedSeries.length>0 ? `${selectedSeries.length} selected` : 'None selected'}
            </div>
            <div className="secondary-actions">
            </div>
          </div>
        </div>
      </div>
      {/* Bottom-right: Map */}
      <div className="graphs-cell map-pane">
        <div className="graphs-cell-header"></div>
        <div className="map-container-inner">
          <FlightMap 
            flightUuid={selectedFlight}
            currentTime={globalTime}
            isLive={shouldUseLiveApi(selectedFlight)}
            onDomain={({ xMin, xMax, launchOffset }) => {
              setDomainStart(prev => (prev == null || xMin < prev ? xMin : prev));
              setDomainEnd(prev => (prev == null || xMax > prev ? xMax : prev));
              const candidateLaunch = (launchOffset != null && Number.isFinite(launchOffset)) ? launchOffset : xMin;
              setLaunchTime(prev => (prev == null || candidateLaunch < prev ? candidateLaunch : prev));
              setFlightEndTime(prev => (prev == null || xMax > prev ? xMax : prev));
              // If we had no launchTime yet, initialize global time at candidate launch
              if (launchTime == null && candidateLaunch != null && Number.isFinite(candidateLaunch)) {
                setGlobalTime(candidateLaunch);
              }
            }}
          />
          {(() => {
            if (selectedFlight) {
              const route = liveRouteRef.current[selectedFlight];
              const isLiveForMap = shouldUseLiveApi(selectedFlight);
              console.log('[FlightMap Props]', { 
                selectedFlight, 
                route, 
                isLiveForMap,
                passedAsIsLive: isLiveForMap
              });
            }
            return null;
          })()}
        </div>
      </div>
      <UploadVideoModal
        open={showVideoModal}
        defaultFlightUuid={modalFlightUuid || selectedFlight || ''}
        flightOptions={flightOptions}
        onClose={()=>setShowVideoModal(false)}
        onUploaded={()=>{ const reloadUuid = modalFlightUuid || selectedFlight || ''; if (reloadUuid) fetchVideos(reloadUuid); setShowVideosPane(true); }}
      />
      <CustomVariableModal
        open={showCustomModal}
        onClose={()=>setShowCustomModal(false)}
        availableColumns={telemetryOptions.map(o=>o.value)}
        onCreate={({ name, expression, dependencies }) => {
          setCustomVariables(prev => prev.some(p=>p.name===name) ? prev : [...prev, { name, expression, dependencies }]);
        }}
      />
      {toastMsg && (
        <div className="fg-toast" role="status" aria-live="polite">{toastMsg}</div>
      )}
      {/* Master Playback Bar */}
      <div className="master-playback-bar" role="group" aria-label="Global playback controls">
        <button
          type="button"
          className="mpb-btn"
          aria-label={playing ? 'Pause playback' : 'Play playback'}
          onClick={()=>setPlaying(p=>!p)}
        >{playing ? 'Pause' : 'Play'}</button>
        <div className="mpb-time">{formatTime(clampedGlobalTime)}</div>
        <input
          type="range"
          className="mpb-slider"
          min={0}
          max={maxDuration}
          step={0.05}
          value={clampedGlobalTime}
          onMouseDown={handleScrubStart}
          onTouchStart={handleScrubStart}
          onChange={handleScrub}
          onMouseUp={handleScrubEnd}
          onTouchEnd={handleScrubEnd}
          aria-valuemin={0}
          aria-valuemax={maxDuration}
          aria-valuenow={clampedGlobalTime}
          aria-label="Global playback position"
        />
        <div className="mpb-time">{formatTime(maxDuration)}</div>
      </div>
    </div>
  );
}

export default FlightGraphs;